/**
 * Maps a framework or regulation to the domain of the body that issues it.
 * Logos are fetched at runtime from Logo.dev by that domain, so the mark shown
 * next to "SOC 2" is the AICPA mark, "ISO 27001" is the ISO mark, and EU
 * regulations resolve to the European Union mark.
 *
 * Frameworks with no sensible issuing-body domain simply return undefined and
 * keep their text-only presentation.
 */

const EXACT_DOMAINS: Record<string, string> = {
  // Security
  "SOC 1": "aicpa-cima.com",
  "SOC 2": "aicpa-cima.com",
  "SOC 3": "aicpa-cima.com",
  "CIS Controls v8": "cisecurity.org",
  FedRAMP: "fedramp.gov",
  "TX-RAMP": "dir.texas.gov",
  StateRAMP: "stateramp.org",
  "CMMC 2.0": "defense.gov",
  TISAX: "enx.com",
  "BSI C5": "bsi.bund.de",
  ENS: "ccn-cert.cni.es",
  "Cyber Essentials": "ncsc.gov.uk",
  IRAP: "cyber.gov.au",

  // Privacy
  GDPR: "edpb.europa.eu",
  "UK GDPR": "ico.org.uk",
  "Data Protection Act 2018": "ico.org.uk",
  "CCPA / CPRA": "cppa.ca.gov",
  "VCDPA": "oag.state.va.us",
  "CPA Colorado": "coag.gov",
  LGPD: "gov.br",
  PIPEDA: "priv.gc.ca",
  POPIA: "inforegulator.org.za",
  "PDPA Singapore": "pdpc.gov.sg",
  APPI: "ppc.go.jp",
  "Saudi PDPL": "sdaia.gov.sa",
  "UAE PDPL": "u.ae",
  "K-ISMS": "kisa.or.kr",
  "Australian Privacy Act": "oaic.gov.au",
  "Ireland DPA 2018": "dataprotection.ie",
  "Switzerland FADP": "edoeb.admin.ch",
  "Türkiye KVKK": "kvkk.gov.tr",
  "India DPDP Act": "meity.gov.in",
  "Thailand PDPA": "pdpc.or.th",
  "Indonesia PDP Law": "komdigi.go.id",
  "Kenya DPA": "odpc.go.ke",
  "Nigeria NDPA": "ndpc.gov.ng",
  "Mexico LFPDPPP": "inai.org.mx",
  "Argentina PDPL": "argentina.gob.ar",

  // Industry
  HIPAA: "hhs.gov",
  "HITRUST CSF": "hitrustalliance.net",
  "PCI DSS 4.0": "pcisecuritystandards.org",
  FERPA: "ed.gov",
  GLBA: "ftc.gov",
  FFIEC: "ffiec.gov",
  "NERC CIP": "nerc.com",
  "GxP / 21 CFR Part 11": "fda.gov",
  SOX: "pcaobus.org",

  // Financial
  "Basel III / IV": "bis.org",
  "FINRA Rule 3110": "finra.org",
  "FINRA Rule 2210": "finra.org",
  "FINRA Rule 2111": "finra.org",
  "Form CRS": "sec.gov",
  "Bank Secrecy Act (BSA)": "fincen.gov",
  "FinCEN Travel Rule": "fincen.gov",
  FATCA: "irs.gov",
  CRS: "oecd.org",
  "AML / CFT": "fatf-gafi.org",
  KYC: "fatf-gafi.org",
  "MAS TRM": "mas.gov.sg",
  "MAS Notice PSN02": "mas.gov.sg",
  "HKMA Operational Resilience": "hkma.gov.hk",
  "APRA CPS 230": "apra.gov.au",
  "APRA CPS 234": "apra.gov.au",
  "AU AML/CTF Act": "austrac.gov.au",
  FINTRAC: "fintrac-canafe.canada.ca",
  "OSFI B-13": "osfi-bsif.gc.ca",
  "NZ AML/CFT Act": "dia.govt.nz",
  "UK EMIR": "fca.org.uk",

  // AI and digital
  "Singapore AI Verify": "aiverifyfoundation.sg",
  "UK AI Safety Framework": "gov.uk",
  "Australia Digital ID Act": "digitalidsystem.gov.au",
  "ASIC Whistleblower": "asic.gov.au",

  // Common shorthand used outside the coverage matrix
  "NIST CSF": "nist.gov",

  // EU regulations resolve to the body that actually supervises them rather
  // than to europa.eu. Three identical EU marks in one pack tile reads as a
  // rendering bug; the ESAs, ENISA and the Commission are also more accurate.
  DORA: "eba.europa.eu",
  NIS2: "enisa.europa.eu",
  "EU AI Act": "commission.europa.eu",

  // Shorthand as it appears on the homepage pack tiles. The coverage matrix
  // uses longer official names, so both spellings have to resolve.
  "CCPA/CPRA": "cppa.ca.gov",
  "India DPDP": "meity.gov.in",
  HITRUST: "hitrustalliance.net",
  "PCI DSS": "pcisecuritystandards.org",
  "FDA 21 CFR Part 11": "fda.gov",
  "CMMC Level 2": "defense.gov",
  "2 CFR §200.501 Single Audit": "whitehouse.gov",
  "WIOA §38.28": "dol.gov",
  "Federal Grant Management Standards": "grants.gov",
};

type Rule = { test: RegExp; domain: string };

const RULES: Rule[] = [
  { test: /^ISO\b/i, domain: "iso.org" },
  { test: /^NIST\b/i, domain: "nist.gov" },
  { test: /^SEC\b/i, domain: "sec.gov" },
  { test: /^FINRA\b/i, domain: "finra.org" },
  { test: /^FCA\b/i, domain: "fca.org.uk" },
  { test: /^SOC \d/i, domain: "aicpa-cima.com" },
  {
    // EU-issued regulations and directives
    test: /^(EU\b|ePrivacy|PSD\d|MiCA|MiFI[DR]|EMIR|MAR$|SFTR|CSDR|UCITS|AIFMD|PRIIPs|AMLD)/i,
    domain: "europa.eu",
  },
];

export const getFrameworkLogoDomain = (name: string): string | undefined => {
  const exact = EXACT_DOMAINS[name];
  if (exact) return exact;
  const rule = RULES.find((r) => r.test.test(name.trim()));
  return rule?.domain;
};
